import { useState, useEffect } from 'react'
import { listEvals } from '../api/vestige.js'

function ScoreBar({ score, passed }) {
  const pct = Math.max(0, Math.min(100, score * 100))
  const barColor = passed ? '#10b981' : score >= 0.5 ? '#f59e0b' : '#ef4444'

  return (
    <div className="flex items-center gap-3">
      <div
        className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Score ${Math.round(pct)}%`}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      <span
        className="text-xs font-mono font-semibold flex-shrink-0 w-10 text-right"
        style={{ color: barColor }}
      >
        {(score * 100).toFixed(0)}%
      </span>
    </div>
  )
}

function avgScore(evals) {
  if (!evals.length) return null
  return evals.reduce((sum, e) => sum + e.score, 0) / evals.length
}

export default function EvalPanel({ snapshot }) {
  const [evals, setEvals] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!snapshot?.id) {
      setEvals([])
      return
    }
    setLoading(true)
    setError(null)
    listEvals(snapshot.id)
      .then(setEvals)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [snapshot?.id])

  const avg = avgScore(evals)
  const passCount = evals.filter((e) => e.passed).length
  const shortHash = snapshot?.snapshotHash?.slice(0, 8) ?? null

  return (
    <div className="border border-border rounded-lg bg-surface overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface2">
        <span className="text-2xs font-mono text-text-faint tracking-widest uppercase">
          Eval results
        </span>
        {shortHash && (
          <span className="text-2xs font-mono text-accent">
            {shortHash}
          </span>
        )}
      </div>

      {!snapshot ? (
        <div className="px-4 py-8 text-center">
          <p className="text-xs text-text-dim">Select a snapshot to see its evals</p>
        </div>
      ) : loading ? (
        <div className="px-4 py-8 text-center">
          <span className="text-xs font-mono text-text-faint">Loading evals…</span>
        </div>
      ) : error ? (
        <div className="px-4 py-4">
          <p className="text-xs text-red">{error}</p>
        </div>
      ) : evals.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-xs text-text-dim">No evals recorded for this snapshot</p>
        </div>
      ) : (
        <>
          {/* Summary row */}
          <div className="px-4 py-4 border-b border-border flex items-center gap-6">
            <div className="flex flex-col gap-0.5">
              <span className="text-2xs font-mono text-text-faint uppercase tracking-widest">
                Avg score
              </span>
              <span
                className="text-2xl font-mono font-semibold"
                style={{
                  color: avg >= 0.8 ? '#10b981' : avg >= 0.5 ? '#f59e0b' : '#ef4444',
                }}
              >
                {(avg * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-2xs font-mono text-text-faint uppercase tracking-widest">
                Passed
              </span>
              <span className="text-lg font-mono font-semibold text-text">
                {passCount}
                <span className="text-text-faint text-sm">/{evals.length}</span>
              </span>
            </div>
          </div>

          {/* Eval table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-2xs font-mono text-text-faint uppercase tracking-widest font-normal">
                    Eval type
                  </th>
                  <th className="text-left px-4 py-2 text-2xs font-mono text-text-faint uppercase tracking-widest font-normal">
                    Score
                  </th>
                  <th className="px-4 py-2 text-2xs font-mono text-text-faint uppercase tracking-widest font-normal text-right">
                    Result
                  </th>
                </tr>
              </thead>
              <tbody>
                {evals.map((ev) => (
                  <tr key={ev.id} className="border-b border-border last:border-0 hover:bg-surface2">
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-text-dim">{ev.evalType}</span>
                    </td>
                    <td className="px-4 py-2.5 min-w-[140px]">
                      <ScoreBar score={ev.score} passed={ev.passed} />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span
                        className={`font-mono text-2xs font-semibold tracking-widest uppercase ${
                          ev.passed ? 'text-green' : 'text-red'
                        }`}
                      >
                        {ev.passed ? 'PASS' : 'FAIL'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
